import { useEffect, useState, useCallback, useRef } from "react";
import { chAnalizarInspeccion, chEstadoAnalisisInspeccion, chGuardarRevisionInspeccion } from "@/lib/prevelis";
import { toast } from "sonner";
import { Sparkles, Loader2, Trash2, Plus, Save, ShieldCheck, AlertTriangle, X } from "lucide-react";

const field = "w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none text-sm";
const PRIORIDADES = ["Immédiate", "Élevée", "Moyenne", "Faible", "À évaluer techniquement",
  "Inmediata", "Alta", "Media", "Baja", "Pendiente de valoración técnica"];
const PRIO_CLS = (p) => {
  const v = (p || "").toLowerCase();
  if (v.includes("inmed") || v.includes("imméd") || v.includes("immed")) return "bg-red-100 text-red-700";
  if (v.includes("alta") || v.includes("élev") || v.includes("elev")) return "bg-orange-100 text-orange-700";
  if (v.includes("media") || v.includes("moy")) return "bg-amber-100 text-amber-700";
  if (v.includes("baja") || v.includes("faible")) return "bg-emerald-100 text-emerald-700";
  return "bg-slate-100 text-slate-600";
};

export default function SuizaAnalisisInspeccion({ inspeccion, onEstado }) {
  const iid = inspeccion.id;
  const [estado, setEstado] = useState("idle");
  const [peligros, setPeligros] = useState([]);
  const [observaciones, setObservaciones] = useState("");
  const [propuesta, setPropuesta] = useState(null);
  const [validado, setValidado] = useState(false);
  const [idioma, setIdioma] = useState("fr");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const poll = useRef(null);

  const cargarRevision = useCallback((rev, prop) => {
    if (prop) setPropuesta(prop);
    if (rev) {
      setPeligros((rev.peligros || []).map((p) => ({ ...p, medidas_correctoras: p.medidas_correctoras || [] })));
      setObservaciones(rev.observaciones || "");
      setValidado(!!rev.validado);
    }
  }, []);

  const consultar = useCallback(async () => {
    try {
      const r = await chEstadoAnalisisInspeccion(iid);
      setEstado(r.estado);
      if (r.estado === "completado") {
        clearInterval(poll.current); poll.current = null;
        cargarRevision(r.revision, r.propuesta);
        onEstado?.(r.inspeccion_estado);
      } else if (r.estado === "error") {
        clearInterval(poll.current); poll.current = null;
        setError(r.error || "Error en el análisis");
      }
    } catch { /* reintentar en el siguiente ciclo */ }
  }, [iid, cargarRevision, onEstado]);

  useEffect(() => {
    consultar();
    return () => { if (poll.current) clearInterval(poll.current); };
  }, [consultar]);

  const analizar = async () => {
    setError("");
    try {
      await chAnalizarInspeccion(iid, idioma);
      setEstado("procesando");
      if (poll.current) clearInterval(poll.current);
      poll.current = setInterval(consultar, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo iniciar el análisis"); }
  };

  const updP = (id, patch) => setPeligros((s) => s.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const delP = (id) => setPeligros((s) => s.filter((p) => p.id !== id));
  const addP = () => setPeligros((s) => [...s, { id: `n-${Date.now()}`, peligro: "", ubicacion: "", expuestos: "", consecuencias: "", medidas_existentes: "", medidas_correctoras: [""], prioridad: "Moyenne", base_normativa: "", origen: "Añadido por el técnico", estado: "manual" }]);
  const updMedida = (id, idx, val) => setPeligros((s) => s.map((p) => p.id === id ? { ...p, medidas_correctoras: p.medidas_correctoras.map((m, i) => i === idx ? val : m) } : p));
  const addMedida = (id) => setPeligros((s) => s.map((p) => p.id === id ? { ...p, medidas_correctoras: [...p.medidas_correctoras, ""] } : p));
  const delMedida = (id, idx) => setPeligros((s) => s.map((p) => p.id === id ? { ...p, medidas_correctoras: p.medidas_correctoras.filter((_, i) => i !== idx) } : p));

  const guardar = async (marcarValidado) => {
    setSaving(true);
    try {
      const limpio = peligros.map((p) => ({ ...p, medidas_correctoras: (p.medidas_correctoras || []).map((m) => m.trim()).filter(Boolean) }));
      const nuevoValidado = marcarValidado != null ? marcarValidado : validado;
      await chGuardarRevisionInspeccion(iid, { peligros: limpio, observaciones, validado: nuevoValidado });
      setValidado(nuevoValidado);
      toast.success(marcarValidado ? "Revisión validada" : "Revisión guardada");
    } catch { toast.error("No se pudo guardar la revisión"); }
    finally { setSaving(false); }
  };

  if (estado === "procesando") {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid="ch-analisis-procesando">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-3" />
        <p className="text-sm text-slate-600">Analizando la inspección con IA… puede tardar hasta 1-2 minutos.</p>
      </div>
    );
  }

  if (estado === "idle" || estado === "error") {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="ch-analisis-inicio">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5" /></div>
          <div className="flex-1">
            <h4 className="font-heading font-semibold">Análisis IA de la inspección</h4>
            <p className="text-sm text-slate-500 mt-1">La IA analiza las no conformidades del checklist, las observaciones y las fotos, y propone los peligros y las medidas correctoras del centro. Después podrás revisar, editar y validar.</p>
            {error && <div className="mt-2 text-sm text-red-600 flex items-start gap-1.5"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}</div>}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <select value={idioma} onChange={(e) => setIdioma(e.target.value)} data-testid="ch-analisis-idioma" className="h-10 px-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500">
                <option value="fr">Français</option>
                <option value="es">Español</option>
              </select>
              <button onClick={analizar} data-testid="ch-analisis-lanzar" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800">
                <Sparkles className="w-4 h-4" /> Analizar con IA
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // completado
  return (
    <div data-testid="ch-analisis-revision">
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex items-center justify-between gap-3 flex-wrap sticky top-2 z-10">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-800">{peligros.length} peligro(s) propuesto(s)</span>
          {validado && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Validado por el técnico</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={analizar} data-testid="ch-analisis-reanalizar" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50"><Sparkles className="w-4 h-4" /> Reanalizar</button>
          <button onClick={() => guardar(null)} disabled={saving} data-testid="ch-analisis-guardar" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-blue-600 text-blue-700 text-sm font-medium hover:bg-blue-50 disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar</button>
          <button onClick={() => guardar(true)} disabled={saving} data-testid="ch-analisis-validar" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"><ShieldCheck className="w-4 h-4" /> Validar</button>
        </div>
      </div>

      {propuesta?.resumen && <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-4 mb-4 text-sm text-slate-700" data-testid="ch-analisis-resumen">{propuesta.resumen}</div>}

      <div className="space-y-3" data-testid="ch-analisis-peligros">
        {peligros.map((p) => (
          <div key={p.id} data-testid={`ch-peligro-${p.id}`} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <input value={p.peligro} onChange={(e) => updP(p.id, { peligro: e.target.value })} data-testid={`ch-peligro-nombre-${p.id}`} className={field + " font-medium"} placeholder="Peligro" />
              <select value={p.prioridad} onChange={(e) => updP(p.id, { prioridad: e.target.value })} data-testid={`ch-peligro-prio-${p.id}`} className={`h-9 px-2 rounded-lg border-0 text-xs font-semibold shrink-0 ${PRIO_CLS(p.prioridad)}`}>
                {PRIORIDADES.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
              <button onClick={() => delP(p.id)} data-testid={`ch-peligro-del-${p.id}`} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              <input value={p.ubicacion || ""} onChange={(e) => updP(p.id, { ubicacion: e.target.value })} className={field} placeholder="Ubicación / puesto" />
              <input value={p.expuestos || ""} onChange={(e) => updP(p.id, { expuestos: e.target.value })} className={field} placeholder="Personas expuestas" />
            </div>
            {p.origen && <div className="mt-2 text-xs text-slate-400">Origen: {p.origen}</div>}
            <input value={p.consecuencias || ""} onChange={(e) => updP(p.id, { consecuencias: e.target.value })} className={field + " mt-2"} placeholder="Consecuencias" />
            <div className="mt-3">
              <div className="text-xs font-semibold text-slate-600 mb-1">Medidas correctoras</div>
              <div className="space-y-1.5">
                {(p.medidas_correctoras || []).map((m, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={m} onChange={(e) => updMedida(p.id, i, e.target.value)} data-testid={`ch-peligro-medida-${p.id}-${i}`} className={field} placeholder="Medida correctora" />
                    <button onClick={() => delMedida(p.id, i)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-500 shrink-0"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <button onClick={() => addMedida(p.id)} data-testid={`ch-peligro-addmedida-${p.id}`} className="mt-1.5 text-xs font-medium text-blue-700 hover:text-blue-800 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Añadir medida</button>
            </div>
            {p.base_normativa && <div className="mt-2 text-xs text-slate-500">Base normativa: {p.base_normativa}</div>}
            {p.pendiente_verificacion && <div className="mt-1 text-xs text-amber-700 flex items-start gap-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {p.pendiente_verificacion}</div>}
          </div>
        ))}
      </div>

      <button onClick={addP} data-testid="ch-peligro-add" className="mt-3 inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-dashed border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50"><Plus className="w-4 h-4" /> Añadir peligro manual</button>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mt-4">
        <div className="text-sm font-semibold text-slate-700 mb-2">Observaciones de la revisión</div>
        <textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)} data-testid="ch-analisis-observaciones" rows={3} className={field} placeholder="Notas del técnico sobre la revisión…" />
      </div>

      {(propuesta?.datos_faltantes?.length > 0 || propuesta?.incertidumbres?.length > 0) && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800" data-testid="ch-analisis-avisos">
          <div className="font-semibold mb-1 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Puntos a completar / verificar</div>
          <ul className="list-disc pl-5 space-y-0.5">
            {[...(propuesta.datos_faltantes || []), ...(propuesta.incertidumbres || [])].map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
